import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Activity,
  Database,
  FileText,
  Mail,
  Loader2,
  CheckCircle,
  AlertCircle,
  Upload,
  RefreshCw,
  Wifi,
  WifiOff,
  TrendingUp,
  Clock,
  Package
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RealTimeDashboard() {
  const { user, getAuthHeaders } = useAuth();
  const [wsConnected, setWsConnected] = useState(true); // Toujours "connecté" via polling
  const [extractionStatus, setExtractionStatus] = useState(null);
  const [pricingStats, setPricingStats] = useState(null);
  const [emailQueueStatus, setEmailQueueStatus] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [supplierFile, setSupplierFile] = useState(null);
  const [supplierName, setSupplierName] = useState("");
  const [importing, setImporting] = useState(false);
  const [catalogs, setCatalogs] = useState([]);
  const pollInterval = useRef(null);

  // Polling au lieu de WebSocket (plus fiable)
  useEffect(() => {
    if (!user?.id) return;

    const pollData = async () => {
      try {
        await Promise.all([
          fetchExtractionStatus(),
          fetchPricingStats(),
          fetchEmailQueueStatus(),
          fetchCatalogs()
        ]);
        setWsConnected(true);
      } catch (error) {
        console.error("Polling error:", error);
      }
    };

    // Premier fetch immédiat
    pollData();
    addActivity("system", "Connexion établie");

    // Polling toutes les 5 secondes
    pollInterval.current = setInterval(pollData, 5000);

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [user?.id]);

  // Fetch functions
  const fetchExtractionStatus = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/pricing/extraction-status`, { headers });
      if (res.data) setExtractionStatus(res.data);
    } catch (error) {
      // Silent
    }
  };

  const fetchPricingStats = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/pricing/stats`, { headers });
      if (res.data) setPricingStats(res.data);
    } catch (error) {
      // Silent
    }
  };

  const fetchEmailQueueStatus = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/email/queue/status`, { headers });
      if (res.data) setEmailQueueStatus(res.data);
    } catch (error) {
      // Silent
    }
  };

  const fetchCatalogs = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/pricing/supplier-catalogs`, { headers });
      if (res.data?.catalogs) setCatalogs(res.data.catalogs);
    } catch (error) {
      // Silent
    }
  };

  const addActivity = useCallback((type, message) => {
    setRecentActivity(prev => [{
      id: Date.now(),
      type,
      message,
      time: new Date().toLocaleTimeString()
    }, ...prev].slice(0, 20));
  }, []);

  const handleSupplierImport = async () => {
    if (!supplierFile) {
      toast.error("Veuillez sélectionner un fichier");
      return;
    }

    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', supplierFile);
      formData.append('supplier_name', supplierName);

      const response = await axios.post(
        `${API}/pricing/import-supplier`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );

      toast.success(`${response.data.items_imported} articles importés`);
      addActivity("import", `Import fournisseur: ${response.data.items_imported} articles`);
      setSupplierFile(null);
      setSupplierName("");
      
      // Refresh catalogs
      const catalogsRes = await axios.get(`${API}/pricing/supplier-catalogs`, { headers: getAuthHeaders() });
      setCatalogs(catalogsRes.data.catalogs || []);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur d'import");
    } finally {
      setImporting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "running": return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case "completed": return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "error": return <AlertCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  return (
    <Layout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
              Dashboard temps réel
            </h1>
            <p className="text-gray-500 mt-1 flex items-center gap-2">
              {wsConnected ? (
                <>
                  <Wifi className="h-4 w-4 text-green-500" />
                  <span className="text-green-600">Connecté</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-4 w-4 text-red-500" />
                  <span className="text-red-600">Déconnecté - Reconnexion...</span>
                </>
              )}
            </p>
          </div>
          <Button 
            variant="outline"
            onClick={() => window.location.reload()}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Extraction Status */}
          <Card className={`${extractionStatus?.status === 'running' ? 'border-blue-300 bg-blue-50/50' : ''}`}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${extractionStatus?.status === 'running' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <Activity className={`h-6 w-6 ${extractionStatus?.status === 'running' ? 'text-blue-600' : 'text-gray-600'}`} />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Extraction automatique</p>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(extractionStatus?.status)}
                    <span className="font-medium">
                      {extractionStatus?.status === 'running' ? 'En cours' : 
                       extractionStatus?.status === 'completed' ? 'Terminée' :
                       extractionStatus?.status === 'error' ? 'Erreur' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              {extractionStatus?.status === 'running' && (
                <div className="mt-3">
                  <Progress value={extractionStatus.percent || 0} className="h-2" />
                  <p className="text-xs text-gray-500 mt-1">
                    {extractionStatus.progress}/{extractionStatus.total} - {extractionStatus.current_file}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pricing Database */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-green-100">
                  <Database className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Base de prix</p>
                  <p className="text-2xl font-bold">{pricingStats?.total_entries || 0}</p>
                  <p className="text-xs text-gray-400">références</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Email Queue */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-purple-100">
                  <Mail className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">File d'envoi d'emails</p>
                  <p className="text-2xl font-bold">{emailQueueStatus?.queued || 0}</p>
                  <p className="text-xs text-gray-400">
                    {emailQueueStatus?.sent_today || 0} envoyés aujourd&apos;hui
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Categories */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-orange-100">
                  <Package className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Catégories</p>
                  <p className="text-2xl font-bold">{pricingStats?.categories?.length || 0}</p>
                  <p className="text-xs text-gray-400">actives</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Supplier Import */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-blue-600" />
                Import tarifs fournisseurs
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Nom du fournisseur (optionnel)</label>
                <Input
                  value={supplierName}
                  onChange={(e) => setSupplierName(e.target.value)}
                  placeholder="Ex: Pépinières ABC"
                  className="mt-1"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Fichier CSV ou Excel</label>
                <Input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => setSupplierFile(e.target.files?.[0] || null)}
                  className="mt-1"
                />
              </div>

              <Button 
                onClick={handleSupplierImport}
                disabled={!supplierFile || importing}
                className="w-full"
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Importer les tarifs
                  </>
                )}
              </Button>

              {/* Recent catalogs */}
              {catalogs.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Catalogues importés</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {catalogs.slice(0, 5).map((cat, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                        <div>
                          <span className="font-medium">{cat.supplier_name}</span>
                          <span className="text-gray-400 ml-2">({cat.filename})</span>
                        </div>
                        <span className="text-green-600">{cat.items_count} articles</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity Feed */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-green-600" />
                Activité en temps réel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {recentActivity.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Aucune activité récente</p>
                ) : (
                  recentActivity.map((activity) => (
                    <div 
                      key={activity.id}
                      className="flex items-start gap-3 p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        activity.type === 'system' ? 'bg-blue-500' :
                        activity.type === 'import' ? 'bg-green-500' :
                        activity.type === 'task_progress' ? 'bg-yellow-500' :
                        'bg-gray-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700">{activity.message}</p>
                        <p className="text-xs text-gray-400">{activity.time}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Categories Overview */}
        {pricingStats?.categories?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#1E8B5A]" />
                Répartition par catégorie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {pricingStats.categories
                  .filter(cat => cat.category && cat.category !== "Divers")
                  .slice(0, 12)
                  .map((cat, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-gray-50 border text-center">
                    <p className="text-sm font-medium text-gray-700 truncate">{cat.category}</p>
                    <p className="text-2xl font-bold text-[#1E8B5A]">{cat.count}</p>
                    <p className="text-xs text-gray-400">références</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
